import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useFilterDraft } from '../../hooks/useFilterDraft'
import { useFilterKeyboardNav } from '../../hooks/useFilterKeyboardNav'
import { useFilterOptions } from '../../hooks/useFilterOptions'
import { IconType } from '../icon/IconType'
import FilterEmptyState from './FilterEmptyState'
import FilterFooter from './FilterFooter'
import FilterOptionList from './FilterOptionList'
import FilterSearchHeader from './FilterSearchHeader'
import { handleFilterRowToggle } from '../../utils/filterSelectionMath'

export type FilterMode = 'single' | 'multi'

export type FilterOptionKind = 'option' | 'toggle'

export interface FilterOption<V = string> {
  value: V
  label: string
  disabled?: boolean
  children?: Array<FilterOption<V>>
  selectable?: boolean
  kind?: FilterOptionKind
  icon?: IconType
}

export type FilterValue<V = string> = V[]

export interface FilterConfig {
  mode: FilterMode
  layered?: boolean
  grouped?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  defaultCollapsed?: boolean
}

export interface FilterLabels {
  apply?: string
  clearAll?: string
  clearSearch?: string
  noResults?: string
  searchPlaceholder?: string
  allItems?: string
  selectedCount?: (count: number) => string
}

export interface IProps<V = string> {
  config: FilterConfig
  options: Array<FilterOption<V>>
  value: FilterValue<V>
  onChange: (next: FilterValue<V>) => void
  onClose?: () => void
  labels?: FilterLabels
  isOpen?: boolean
  className?: string
  ariaLabel?: string
  totalLabel?: string
}

let filterIdCounter = 0
const useFilterIds = () => {
  const ref = useRef<{ search: string; listbox: string } | null>(null)
  if (!ref.current) {
    filterIdCounter += 1
    ref.current = {
      search: `filter-search-${filterIdCounter}`,
      listbox: `filter-listbox-${filterIdCounter}`,
    }
  }
  return ref.current
}

const Filter = <V,>({
  config,
  options,
  value,
  onChange,
  onClose,
  labels: labelsProp,
  isOpen = true,
  className,
  ariaLabel,
  totalLabel,
}: IProps<V>): JSX.Element => {
  const { t } = useTranslation()
  const labels: Required<FilterLabels> = {
    apply: t('general.actions.apply'),
    clearAll: t('general.forms.clearFilter'),
    clearSearch: t('general.forms.clearSearch'),
    noResults: t('general.forms.emptySearch'),
    searchPlaceholder: t('general.actions.search'),
    allItems: t('general.all'),
    selectedCount: (count: number) => `${count} ${t('general.selected')}`,
    ...labelsProp,
  }
  const { search: searchId, listbox: listboxId } = useFilterIds()
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const rowRefs = useRef<Map<unknown, HTMLElement>>(new Map())

  const {
    draft,
    searchTerm,
    setSearchTerm,
    toggleRow,
    clear,
    isDirty,
    isCleared,
  } = useFilterDraft<V>({
    applied: value,
    mode: config.mode,
    isOpen,
  })

  const [expanded, setExpanded] = useState<Set<unknown>>(() => {
    const next = new Set<unknown>()
    if (config.defaultCollapsed) return next
    options.forEach((option) => {
      if (option.children && option.children.length > 0) {
        next.add(option.value)
        if (config.grouped) {
          option.children.forEach((child) => {
            if (child.children && child.children.length > 0) {
              next.add(child.value)
            }
          })
        }
      }
    })
    return next
  })

  const toggleExpand = useCallback((rowValue: V) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(rowValue)) next.delete(rowValue)
      else next.add(rowValue)
      return next
    })
  }, [])

  const setRowRef = useCallback((rowValue: V, el: HTMLElement | null) => {
    if (el) rowRefs.current.set(rowValue, el)
    else rowRefs.current.delete(rowValue)
  }, [])

  const { visibleOptions, hasResults } = useFilterOptions<V>({
    options,
    searchTerm,
  })

  const effectiveExpanded = useMemo(() => {
    if (!searchTerm.trim()) return expanded
    const next = new Set<unknown>(expanded)
    const collectParents = (opts: Array<FilterOption<V>>) => {
      opts.forEach((o) => {
        if (o.children && o.children.length > 0) {
          next.add(o.value)
          collectParents(o.children)
        }
      })
    }
    collectParents(visibleOptions)
    return next
  }, [expanded, searchTerm, visibleOptions])

  const handleApply = useCallback(() => {
    onChange(draft)
    onClose?.()
  }, [onChange, draft, onClose])

  const handleCancel = useCallback(() => {
    onClose?.()
  }, [onClose])

  // One choice needs no apply step: picking a row applies it and closes the panel. Multi
  // keeps the draft until Apply.
  const single = config.mode === 'single'
  const handleToggle = useCallback(
    (option: FilterOption<V>) => {
      if (!single) {
        toggleRow(option)
        return
      }
      const next = handleFilterRowToggle(option, value, 'single')
      // Unchanged means the row can't be chosen: stay open
      if (next === value) return
      onChange(next)
      onClose?.()
    },
    [single, toggleRow, value, onChange, onClose]
  )

  const { activeValue, handleKeyDown } = useFilterKeyboardNav<V>({
    options: visibleOptions,
    expanded: effectiveExpanded,
    isOpen,
    onToggle: handleToggle,
    onToggleExpand: toggleExpand,
    onCommit: handleApply,
    onCancel: handleCancel,
    rowRefs: rowRefs.current,
  })

  useEffect(() => {
    if (!isOpen) return
    if (config.searchable) {
      searchInputRef.current?.focus({ preventScroll: true })
    } else {
      containerRef.current?.focus({ preventScroll: true })
    }
  }, [isOpen, config.searchable])

  const totalSelectedLabel = useMemo(() => {
    if (totalLabel) return totalLabel
    if (draft.length === 0) return labels.allItems
    return labels.selectedCount(draft.length)
  }, [draft.length, labels, totalLabel])

  const showEmptyState = config.searchable && !!searchTerm && !hasResults
  const applyDisabled = !isDirty || (isCleared && value.length === 0)
  const clearDisabled = draft.length === 0
  const applyHighlighted = isDirty && !isCleared

  return (
    <Panel
      ref={containerRef}
      className={className}
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {config.searchable && (
        <FilterSearchHeader
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={config.searchPlaceholder ?? labels.searchPlaceholder}
          inputId={searchId}
          listboxId={listboxId}
          inputRef={searchInputRef}
        />
      )}
      {showEmptyState ? (
        <FilterEmptyState
          message={labels.noResults}
          clearLabel={labels.clearSearch}
          onClear={() => setSearchTerm('')}
        />
      ) : (
        <FilterOptionList<V>
          listboxId={listboxId}
          options={visibleOptions}
          mode={config.mode}
          grouped={!!config.grouped}
          draft={draft}
          expanded={effectiveExpanded}
          activeValue={activeValue}
          totalSelectedLabel={totalSelectedLabel}
          onToggle={handleToggle}
          onToggleExpand={toggleExpand}
          setRowRef={setRowRef}
        />
      )}
      {!single && (
        <FilterFooter
          clearLabel={labels.clearAll}
          applyLabel={labels.apply}
          onClear={clear}
          onApply={handleApply}
          applyDisabled={applyDisabled}
          clearDisabled={clearDisabled}
          applyHighlighted={applyHighlighted}
        />
      )}
    </Panel>
  )
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 320px;
  outline: 0;
  background-color: ${({ theme }) => theme.colors.lightest};
`

export default Filter
